import React from "react";

import {
	faAt,
	faCheckCircle,
	faCheckSquare,
	faIcons,
	faListOl,
	faListUl,
	faPhotoVideo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCommentAltLines, faGrid2Plus, faSmile } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import classNames from "classnames";
import { compact, noop } from "lodash";
import moment from "moment";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { Button } from "semantic-ui-react";
import uuid from "uuid";

import { integrations } from "~/integrations/actionItemIntegration";
import { EditorConfig, Extension, ProseReact } from "~/lib/prose";
import { setBlockType } from "~/lib/prose/commands";
import type { MenuEntry } from "~/lib/prose/extension";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	HardBreak,
	Header as HeaderNode,
	HorizontalRule,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import {
	Agenda,
	DropTarget,
	Goal,
	Indent,
	InputRules,
	MentionProvider,
	MoveSelection,
	MultiSelect,
	MultiSelectDropHandler,
	Placeholder,
	Todo,
	TypeAhead,
} from "~/lib/prose/plugins";
import { BlockActions } from "~/lib/prose/plugins/blockActions";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import { CollapseExtension } from "~/lib/prose/plugins/collapse";
import { FileDropHandler, NoteTaskDropHandler } from "~/lib/prose/plugins/dropTarget";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import MousePos from "~/lib/prose/plugins/mousePos";
import { PromptExtension } from "~/lib/prose/plugins/prompt";
import { choiceToTypeaheadResult } from "~/lib/prose/plugins/slash";
import Toolbar from "~/lib/prose/plugins/toolbar";
import { TypeAheadProvider, type TypeAheadResult, type TypeAheadState } from "~/lib/prose/plugins/typeahead";
import store from "~/lib/store";
import { copyToClipboard, uiAvatar } from "~/lib/utils";
import type { AssignableDropdownMenuSections } from "~/notes/AssignableDropdown";
import Loader from "~/public/Loader";
import NotFound from "~/public/NotFound";

import styles from "./css/Template.module.css";

const TOOLBAR_MOUNT_POINT_ELEMENT_ID = "toolbar-mount-point";

function sendSignupIntentToParent(intent?: string) {
	try {
		window.top?.postMessage({ type: "PUBLIC_TEMPLATE_SIGNUP_INTENT", intent: intent }, "*");
	} catch (_error) {
		console.error(_error);
	}
}

type TemplateProps = { match: { params: { templateGlobalKey: string } } };

const postProcessBlockActions = (
	closeMenu: () => void,
	_existingMenuSections: (cm: () => void) => AssignableDropdownMenuSections,
) => {
	return [
		{
			children: [
				{
					id: "add-reaction",
					icon: faSmile,
					right: "Add a reaction…",
					action: () => {
						sendSignupIntentToParent("add-reaction");
						closeMenu();
						return false;
					},
				},
				{
					id: "addComments",
					icon: faCommentAltLines,
					right: "Add a comment…",
					action: () => {
						sendSignupIntentToParent("add-comment");
						closeMenu();
						return false;
					},
				},
			],
		},
		{
			children: [
				...integrations
					.map(integration => {
						return {
							id: integration.key,
							left: integration.logo,
							right: `${integration.displayName}`,
							action: () => {
								sendSignupIntentToParent(`integration-${integration.key}`);
								closeMenu();
								return false;
							},
						};
					})
					.filter(integration => !["JIRA"].includes(integration.id)),
				{
					id: "integrations",
					icon: faGrid2Plus,
					right: "Browse more integrations…",
					action: () => {
						sendSignupIntentToParent("browse-integrations");
						closeMenu();
						return false;
					},
				},
			],
		},
	];
};

function postProcessMenuEntries(existingMenuEntries: MenuEntry[]) {
	return compact(
		existingMenuEntries.reduce((acc: MenuEntry[], entry) => {
			if (entry.title == "Goal") return acc;
			if (entry.title == "Attachments strip")
				return [
					...acc,
					{
						...entry,
						run: (_m: Toolbar) => {
							return () => {
								sendSignupIntentToParent(`menu-${entry.title}`);
								return true;
							};
						},
					},
				];
			return [...acc, entry];
		}, [] as MenuEntry[]),
	);
}

const onMenuOrTypeAheadResultClickOverride = (item: TypeAheadResult, next: () => void) => {
	if (
		[
			"ClickUp",
			"Trello",
			"monday.com",
			"Linear",
			"Microsoft To Do",
			"Asana",
			"Embed media",
			"Jira",
			"Attachments strip",
			"Key result",
		].includes(item.title)
	) {
		sendSignupIntentToParent(`menu-${item.title}`);
		return false;
	}
	if (item.title.includes("Section")) {
		sendSignupIntentToParent();
		return false;
	}
	return next();
};

class PublicTemplateTypeAheadProvider extends TypeAheadProvider {
	static items = [
		{
			title: "Talking Point",
			icon: faCheckCircle,
			content: "Add a discussion item.",
			id: "talking-point",
			command: (state: EditorState) => {
				return setBlockType(state.schema.nodes["agenda"], {});
			},
		},
		{
			title: "Action Item",
			icon: faCheckSquare,
			id: "action-item",
			content: "Add, assign, schedule tasks.",
			command: (state: EditorState) => {
				return setBlockType(state.schema.nodes["todo"], {});
			},
		},
		{
			id: "bullet",
			title: "Bullet",
			icon: faListUl,
			content: "Add a bulleted list.",
			showInToolbar: true,
			showOnlyOnExtraSmallScreen: true,
			command: (state: EditorState) => {
				if ("bullet-point" in state.schema.nodes) {
					return setBlockType(state.schema.nodes["bullet-point"], { indent: 1 });
				}
				return setBlockType(state.schema.nodes["unstyled"], {
					indent: 1,
				});
			},
		},
		{
			id: "ol",
			title: "Numbered List",
			icon: faListOl,
			content: "Add a numbered list.",
			command: (state: EditorState) => {
				if ("numbered-list-item" in state.schema.nodes) {
					return setBlockType(state.schema.nodes["numbered-list-item"], { indent: 1 });
				}
				return () => {
					sendSignupIntentToParent();
					return true;
				};
			},
		},
		{
			id: "em",
			title: "Embed media",
			icon: faIcons,
			content: "Insert media from other websites.",
			command: () => {
				return () => {
					sendSignupIntentToParent();
					return true;
				};
			},
		},
		{
			id: "image-strip",
			title: "Attachments strip",
			content: "Add files.",
			icon: faPhotoVideo,
			command: (_state: EditorState, _typeaheadState?: TypeAheadState) => {
				return () => {
					sendSignupIntentToParent();
					return true;
				};
			},
		},
		{
			id: "mention",
			title: "Mention",
			icon: faAt,
			content: "Mentions send notifications.",
			command: () => {
				return (state: EditorState, dispatch: (tr: Transaction) => void) => {
					dispatch(state.tr.insertText("@"));
					return false;
				};
			},
		},
	].map(item => choiceToTypeaheadResult(item, item.id));
	get key() {
		return "/";
	}
	search(_view: EditorView, searchString: string, _state: TypeAheadState, _plugin: TypeAhead): TypeAheadResult[] {
		const allItems = PublicTemplateTypeAheadProvider.items;
		if (searchString === "") {
			return allItems;
		}

		const items = [];

		for (const item of allItems) {
			const score = this.fuzzy(item.title, searchString);
			if (score === undefined || score === -Infinity) continue;
			items.push({
				...item,
				order: () => -score,
			});
		}

		return items;
	}
}

const getTemplateEditorConfig = (
	schemaVersion: number,
	editable = false,
	disablePrompt = false,
	forContentCreation = false,
	forceDesktopDropdown = false,
) => {
	let extensions: Extension[] = [
		new BlockBoundary(),
		...getAllMarks({
			linkEditable: editable,
			placeholderEditable: forContentCreation,
			disableLinkPill: schemaVersion < 2,
		}),
		new TextNode(),
		new Indent(),
		new Unstyled({ showBullet: schemaVersion < 2 }),
		new Goal(),
		...(schemaVersion >= 2 ? [new BulletPoint(), new NumberedListItem()] : []),
		...(schemaVersion >= 3 ? [new HorizontalRule()] : []),
		new HardBreak(),
		new HeaderNode({ inTemplate: true, inCustomSection: true }),
		new CollapseExtension([]),
		new ImageStrip({ contentId: "", disable: !editable }),
		new ImageStripItem(),
		new ImageStripItemFile(),
		new ImageStripItemCaption(),
		new Tag({ showToolbar: false, clickable: false }),
		new Todo({ disableJira: true, localToggle: true, disabledReadOnly: true, isPublicTemplateView: true }),
		new Agenda({ localToggle: true, disabledReadOnly: true, isPublicTemplateView: true }),
		new Mention(),
		new Placeholder(),
		...(!disablePrompt ? [new PromptExtension({ inTemplate: true })] : []),
		...(schemaVersion >= 2 ? [new PasteBuiltInEmoji()] : []),
	];

	if (editable) {
		extensions = extensions.concat([
			new Toolbar({
				noJira: true,
				noMention: true,
				mountPointId: TOOLBAR_MOUNT_POINT_ELEMENT_ID,
				onChoiceClickOverride: onMenuOrTypeAheadResultClickOverride,
				postProcessEntries: postProcessMenuEntries,
			}),
			new MoveSelection(),
			new DropTarget({
				handlers: [new NoteTaskDropHandler(), new MultiSelectDropHandler(), new FileDropHandler(noop)],
			}),
			new MultiSelect({ selectionTabClass: classNames(styles["multiselect-drag-handle"], "multiselect-drag-handle") }),
			new MousePos(),
			new TypeAhead({
				providers: [new PublicTemplateTypeAheadProvider(), new MentionProvider(), new EmojiProvider()],
			}),
			new InputRules(),
			new BlockActions({ postProsessMenuSections: postProcessBlockActions, forceDesktopDropdown }),
		]);
	}

	return new EditorConfig({
		extensions,
		nodes: {
			doc: {
				content: "block+",
			},
		},
	});
};

const publicTemplateStore = store.namespace("publicTemplateContent");
function makeStoreKey(key: string) {
	return `content.${key}`;
}

export function hasPublicTemplateContent(globalKey: string) {
	return publicTemplateStore.has(makeStoreKey(globalKey));
}

export function getPublicTemplateContent(globalKey: string): Record<string, unknown> | undefined {
	const result = publicTemplateStore.get(makeStoreKey(globalKey));
	if (result) {
		publicTemplateStore.expire(makeStoreKey(globalKey), moment().add(7, "day"));
	}
	return result;
}

function setPublicTemplateContent(globalKey: string, content: unknown) {
	publicTemplateStore.setExpire(makeStoreKey(globalKey), content, moment().add(7, "day"));
}

export function getAndClearPublicTemplateContent(globalKey: string) {
	const result = getPublicTemplateContent(globalKey);
	publicTemplateStore.remove(makeStoreKey(globalKey));
	return result;
}

function PublicTemplateContentCreationTool({
	getEditorView,
	editorConfig,
	setDoc,
	templateGlobalKey,
}: {
	templateGlobalKey: string;
	editorConfig: EditorConfig;
	getEditorView?: () => EditorView | null | undefined;
	setDoc?: (doc: ProseNode) => void;
}) {
	const onReset = React.useCallback(() => {
		if (!setDoc) return;
		let data = new FormData();
		fetch(`/public/templates/${templateGlobalKey}/`, {
			method: "POST",
			body: data,
			headers: {
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then((res: Response) => {
				return res.json();
			})
			.then(({ template }: { template: FellowTemplate }) => {
				try {
					const node = ProseNode.fromJSON(editorConfig.schema, template.prose_doc);
					setDoc?.(node);
				} catch (error) {
					console.error(error);
				}
			});
	}, [editorConfig, setDoc, templateGlobalKey]);
	const onCopyJSON = React.useCallback(() => {
		const editorView = getEditorView?.();
		if (!editorView) return;
		const json = editorView.state.doc.toJSON();
		copyToClipboard(JSON.stringify(json, null, 0));
	}, [getEditorView]);
	return (
		<div className="">
			<Button onClick={onCopyJSON}>Copy JSON</Button>
			<Button onClick={onReset}>Reset content</Button>
		</div>
	);
}

type PublicTemplateEvent =
	| {
			type: "TEMPLATE_CONTENT";
			id?: string;
			content: { [key: string]: unknown };
	  }
	| { type: "_OTHER_" };

export function PublicTemplate({
	templateGlobalKey,
	editable = false,
	usingLocalState = false,
	forContentCreation = false,
	forceDesktopDropdown = false,
}: {
	templateGlobalKey: string;
	editable?: boolean;
	usingLocalState?: boolean;
	forContentCreation?: boolean;
	forceDesktopDropdown?: boolean;
}) {
	const localStateChanged = React.useRef(hasPublicTemplateContent(templateGlobalKey));

	const [reRenderKey, setReRenderKey] = React.useState<number>(0);
	const [template, setTemplate] = React.useState<FellowTemplate | null>(null);
	const [loading, setLoading] = React.useState<boolean>(true);

	const selfId = React.useRef<string>(uuid.v4());
	const proseReactRef = React.useRef<ProseReact>(null);
	const getEditorView = React.useCallback(() => {
		return proseReactRef.current?.editorView;
	}, []);
	const setDoc = React.useCallback(
		(doc: ProseNode) => {
			if (!template) return;
			// eslint-disable-next-line camelcase
			template.prose_doc = doc.toJSON();
			setReRenderKey(k => k + 1);
		},
		[setReRenderKey, template],
	);

	React.useEffect(() => {
		if (!usingLocalState) return;
		if (!template) return;
		function listener(e: MessageEvent<PublicTemplateEvent>) {
			if (!e.isTrusted) return;
			const { data } = e;
			if (data.type === "TEMPLATE_CONTENT" && template) {
				if (localStateChanged.current) return;
				if (data.id && data.id !== selfId.current) return;
				// eslint-disable-next-line camelcase
				template.prose_doc = data.content;
				// we want to always persist the content if it's override content from marketing
				setPublicTemplateContent(templateGlobalKey, data.content);
				setReRenderKey(k => k + 1);
			}
		}
		try {
			window.top?.postMessage(
				{ type: "PUBLIC_TEMPLATE_INSTANCE_ID", id: selfId.current, globalKey: templateGlobalKey },
				"*",
			);
		} catch (_e) {}
		window.addEventListener("message", listener);
		return () => {
			window.removeEventListener("message", listener);
		};
	}, [template, templateGlobalKey, usingLocalState]);

	const onChange = React.useCallback(
		(editorState: EditorState, changed, _, oldState?: EditorState) => {
			if (!changed) return;
			if (!oldState) return; // initial render
			if (usingLocalState) {
				setPublicTemplateContent(templateGlobalKey, editorState.doc.toJSON());
				localStateChanged.current = true;
			}
		},
		[templateGlobalKey, usingLocalState],
	);

	React.useEffect(() => {
		let data = new FormData();
		setLoading(true);
		fetch(`/public/templates/${templateGlobalKey}/`, {
			method: "POST",
			body: data,
			headers: {
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then((res: Response) => {
				return res.json();
			})
			.then(({ template }: { template: FellowTemplate }) => {
				if (usingLocalState) {
					const localState = getPublicTemplateContent(templateGlobalKey);
					if (localState) {
						// eslint-disable-next-line camelcase
						template.prose_doc = localState;
					}
				}
				setTemplate(template);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	}, [setTemplate, setLoading, templateGlobalKey, usingLocalState]);
	const editorConfig = React.useMemo(() => {
		if (!template) return getTemplateEditorConfig(1, editable, true);
		return getTemplateEditorConfig(template.schema_version, editable, true, forContentCreation, forceDesktopDropdown);
	}, [editable, forContentCreation, forceDesktopDropdown, template]);

	if (loading) return <Loader loading={loading} />;
	if (!template) return <NotFound />;

	return (
		<div className={classNames(styles.template, { [styles.readonly]: !editable })}>
			<div id={TOOLBAR_MOUNT_POINT_ELEMENT_ID} className={styles.toolbarContainer} />
			<ProseReact
				className={styles.editor}
				ref={proseReactRef}
				editable={editable}
				key={reRenderKey}
				config={editorConfig}
				initialDoc={template.prose_doc}
				enableReactHost
				onChange={onChange}
				context={{
					isPublicTemplateView: true,
					noteId: "a",
					user: {
						id: "a",
					},
					participants: ["Jerry Parker", "Kathryn Smith", "Stephanie Bartlett", "Jason Horton"].map((name, index) => ({
						id: index,
						email: `${name.replace(/\s/g, "").toLowerCase()}@yourcompany`,
						firstName: name.split(" ")[0],
						fullName: name,
						image: uiAvatar(name),
					})),
				}}
			/>
			{forContentCreation && (
				<PublicTemplateContentCreationTool
					getEditorView={getEditorView}
					editorConfig={editorConfig}
					templateGlobalKey={templateGlobalKey}
					setDoc={setDoc}
				/>
			)}
		</div>
	);
}

function TemplateRoute(props: TemplateProps) {
	const usingLocalState = window.location.search.includes("state=local");
	const forContent = window.location.search.includes("for_marketing=true");
	const forceDesktopDropdown = window.location.search.includes("desktop_dropdown=true");
	return (
		<PublicTemplate
			templateGlobalKey={props.match.params.templateGlobalKey}
			editable
			usingLocalState={usingLocalState}
			forContentCreation={forContent}
			forceDesktopDropdown={forceDesktopDropdown}
		/>
	);
}

export default TemplateRoute;
